import {
  AriaAttributes,
  CSSProperties,
  Dispatch,
  ReactElement,
  SetStateAction,
  FocusEvent,
  ReactNode,
} from 'react';
import { DragSourceMonitor, DropTargetMonitor } from 'react-dnd';
import { BoxColor, DesignTokens } from '../../../theme';
import { TestId, TestIdProps } from '../../../util/testId';
import { IconProps } from '../../Icon';
import { BaseInputProps } from '../../Input/__internal__/BaseInput';
import { ItemLocation, MenuSection } from '../../Menu';
import { MenuButtonProps } from '../../MenuButton';
import { ToolbarProps } from '../../Toolbar';

export type TabBarVisualStyle = 'standard' | 'alternative' | 'standard-small';
export type TabBarOrientation = 'horizontal' | 'vertical';
export type TabBarPlacement = 'start' | 'end';
export type TabBarRole = 'group' | 'navigation' | 'region' | undefined;

export const TabDragType = 'NOVA_TAB';

export enum TabBarVariant {
  Standard = 'standard',
  PrimaryLight = 'primary-light',
}

export type TabState =
  | 'processing'
  | 'alert'
  | 'unsaved'
  | 'ready'
  | 'hidden'
  | 'readOnly'
  | 'count';

// DND allows re-order of
export type TabItem = {
  id?: string;
  label?: string;
  icon?: IconProps;
  message?: BaseInputProps['message'];
  status?: BaseInputProps['status'];
  hidden?: boolean;
  state?: TabState | TabState[];
  count?: number;
  closable?: boolean;
  disabled?: boolean;
  altId?: string;
  menuSections?: ReadonlyArray<MenuSection>;
  hideTabMenuButton?: boolean;
  allowDrag?: boolean;
  movableTab?: boolean;
  className?: string;
  style?: CSSProperties;
  testId?: TestId;
  menuButtonTooltip?: string;
  closeTabButtonTooltip?: string;
  closeTabButtonkeyboardTooltip?: string;
  ariaControls?: string;
};

export type TabTokens = (
  dt: DesignTokens,
  props: {
    selected: boolean;
    alternate: boolean;
    standardSmall: boolean;
    variant: TabBarVariant;
    orientation?: TabBarOrientation;
  }
) => Partial<BoxColor> & {
  'bg-hover'?: string;
  'item-font-size'?: string;
};
export type TabBarContainerTokens = (
  dt: DesignTokens,
  props: { alternate: boolean; variant: TabBarVariant }
) => { bd?: string; bg?: string };

export type TabEditActionType = 'Change' | 'Commit' | 'Cancel';

export type OverflowMenuButtonDisplayOptions = 'always' | 'never' | 'onVisibleScrollbar';

export type AddNewButtonProps = {
  icon: Pick<IconProps, 'icon'>;
};

export interface BaseTabBarProps extends TestIdProps {
  altId?: string;
  focusedIndex: number;
  setFocusedIndex: Dispatch<SetStateAction<number>>;
  className?: string;
  style?: CSSProperties;
  selectedIndex: number;
  items: TabItem[];
  onItemSelected: (e: { index: number; item: TabItem }) => void;
  variant?: TabBarVariant;
  visualStyle?: TabBarVisualStyle;
  // FIXME: TabBar no longer requires overrides. Keeping this in place for now to prevent build issues.
  tokenOverrides?: TabBarContainerTokens;
  tabOverrides?: TabTokens;
  // Used when the TabBar acts as the transition point between themes in hybrid themes.
  themeTransition?: boolean;
  editable?: boolean;

  /**
   * Option to have custom "Rename" menu with its own 'Rename' implementation in such case 'showRenameMenuOption' should have value as false
   * Default value will be true.
   */
  showRenameMenuOption?: boolean;

  /**
   * Option to have custom "Close tab" menu with its own 'Close tab' implementation in such case 'showCloseMenuOption' should have value as false
   * Default value will be true.
   */
  showCloseMenuOption?: boolean;
  hideCloseAllTabOption?: boolean;
  showContextMenu?: boolean;
  stretchToFit?: boolean;
  closeTabButtonTooltip?: string;
  closeTabButtonkeyboardTooltip?: string;
  menuButtonTooltip?: string;
  onTabLabelChange?: (
    itemIndex: number,
    item: TabItem,
    value: string,
    type: TabEditActionType
  ) => void;
  onTabMenuItemSelect?: (itemIndex: number, item: TabItem, location: ItemLocation[]) => void;
  onTabMenuOpen?: (itemIndex: number, item: TabItem) => void;
  addNewTabButtonProps?: {
    onPress: () => void;
    ariaLabel?: string;
    title?: string;
  };

  renderAddNewTabButton?: (buttonProps: AddNewButtonProps) => ReactElement;
  orientation?: TabBarOrientation;
  /**
   * If tabbar is editable then consumer can specify the postion of the "Rename" menu with renameMenuItemPosition props.
   */
  renameMenuItemPosition?: number;
  // these below props only affect the UI in vertical orientation mode
  placement?: TabBarPlacement;
  headerElement?: () => ReactElement;
  expandCollapseProps?: {
    isExpanded: boolean;
    onExpand: () => void;
    expandTooltip?: string;
    collapseTooltip?: string;
    expandAriaLabel?: string;
    collapseAriaLabel?: string;
  };
  // use to hide selection indicator in vertical mode
  showSelectionIndicator?: boolean;
  role?: TabBarRole;
  ariaLabel?: string;
  landmarkLabel?: string;
  /**
   * Used to identify unique drop zones when content may be dragged from one tabbar to another.
   */
  dndPrefix?: string;

  allowDrop?: boolean;
  /**
   * Used to identify unique drag zone when content may be dragged.
   * @default 'NOVA_TAB'
   */
  dragType?: string;

  /**
   * The `dropTypes` is an object prop with the two props i.e. `tabs` and `content`.
   *
   * A `tabs` is a prop which is an array of strings, **Used to support the DnD of tabs across the tabbar.**
   * A `content`, is a prop which is an array of strings, **Used to support a UI component(other than Tab) as a content drop on the tab of tabbar.**
   */
  dropTypes?: { tabs?: string[]; content?: string[] };

  /**
   * The `onDrop` is a callback function prop that will be used when a consumer performs a drop operation.
   */
  onDrop?: (e: DropEvent) => void;

  /**
   * The `onDragEnd` is a callback function prop that will be used after onDrop to signal the completion of the drag operation.
   */
  onDragEnd?: (e: DragEndEvent) => void;

  /**
   * The 'reorderTabs' is a callback function prop that will be used for reordering of tabs inside tabbar.
   */
  reorderTabs?: (oldTabIndex: number, newTabIndex: number) => void;
  // close Tab
  closeTabMenuItemText?: string;
  closeAllTabMenuItemText?: string;
  onTabClose?: (itemIndex: number) => void;
  onTabCloseAll?: () => void;
  // overflow menu
  overflowMenuButtonDisplay?: OverflowMenuButtonDisplayOptions;
  // It made sense to me to externalize the MenuButton using prop getters for consumers
  // who would like to plug-in cusom Menu or Popover as it makes TabBar free from
  // maintaining the state related to it.
  renderOverflowMenuButton?: (
    buttonProps: Pick<MenuButtonProps, 'testId' | 'text' | 'tooltip' | 'ariaLabel' | 'icon'>,
    itemsMenuProps: {
      onItemSelected: (index: number) => void;
      onItemClosed: (index: number) => void;
      items: BaseTabBarProps['items'];
      selectedIndex: number;
    }
  ) => ReactElement;
  overflowMenuButtonPosition?: 'endOfTabBar' | 'endOfTabList';
  overflowMenuButtonTooltip?: string;
  overflowMenuButtonAriaLabel?: AriaAttributes['aria-label'];

  renderTabToolbar?: () => ReactElement<ToolbarProps>;
  toolbarWidth?: CSSProperties['width'];
}

/**
 * Don't use Tab outside of Tabbar, please use the Tabbar instead.
 */
export interface TabProps extends TestIdProps {
  testId?: TestId;
  altId?: string;
  id?: string;
  ariaControls?: string;
  label?: string;
  selected: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;

  /**
   * variant is not supported in legacy themes (not using Nova tokens)
   */
  variant?: TabBarVariant;
  visualStyle?: TabBarVisualStyle;

  /**
   * This property is used to display hidden tab state indicator if turned 'on'.
   * This property is `deprecated` and is replaced by `state` property.
   * if the state property is specified this property will be ignored
   * @deprecated since version 23.0.0
   * @see state
   */
  hidden?: boolean;
  /**
   * The `state` represents tab state by rendering an appropriate indicator in the tab.
   * A consumer needs to provide state value from predefined literal type named TabState
   * The priority of states will based on consumers input.
   * 1. Processing
   * 2. Alert
   * 3. Unsaved
   * 4. Ready
   * 5. Hidden
   * 6. Read-only
   * 7. Count
   *
   * The UX design team strongly recommends that solutions should follow the order as mentioned above while displaying state indicators.
   */
  state?: TabState | TabState[];
  count?: number;
  location?: 'top' | 'bottom' | 'left' | 'right';
  orientation?: TabBarOrientation;
  textStyle?: CSSProperties;
  tokenOverrides?: TabTokens;
  icon?: IconProps;
  focused?: boolean;
  tabIndex?: number;
  editable?: boolean;
  closable?: boolean;
  message?: BaseInputProps['message'];
  status?: BaseInputProps['status'];
  closeTabMenuItemText?: string;
  closeAllTabMenuItemText?: string;
  renameMenuItemPosition?: number;
  showRenameMenuOption?: boolean;
  showCloseMenuOption?: boolean;
  hideCloseAllTabOption?: boolean;
  showContextMenu?: boolean;
  menuProps?: {
    menuSections: ReadonlyArray<MenuSection>;
    onMenuItemSelect: (location: ItemLocation[], menuSections?: ReadonlyArray<MenuSection>) => void;
    onOpen?: MenuButtonProps['onOpen'];
  };
  hideTabMenuButton?: boolean;
  stretchToFitTab?: boolean;
  closeTabButtonTooltip?: string;
  closeTabButtonkeyboardTooltip?: string;
  menuButtonTooltip?: string;
  allowDrag?: boolean;
  movableTab?: boolean;
  onFocus?: (event: FocusEvent<HTMLDivElement>) => void;
  onPress?: () => void;
  onLabelChange?: (newValue: string, type: TabEditActionType) => void;
  onClose?: () => void;
  onCloseAll?: () => void;
}

export interface DraggableTabProps {
  id: string;
  children: ReactNode;
  type: string;
  dndPrefix: string;
  index: number;
  allowDrag: boolean;
  allowDrop: boolean;
  acceptDropTypes?: string[];
  droppableContent: string[];
  reorderTabs?: (oldTabIndex: number, newTabIndex: number) => void;
  onDragEnd?: (e: DragEndEvent) => void;
  onDrop?: (e: DropEvent) => void;
  onFocus?: () => void;
  orientation?: TabBarOrientation;
}

export interface DragEndEvent {
  monitor?: DragSourceMonitor;
  draggdItem: DraggableTabProps;
  dragIndex: number;
}

export interface DropEvent {
  monitor?: DropTargetMonitor;
  droppedItem: DraggableTabProps;
  dropIndex: number;
}
